<template>
  <div
      class="schedule-step"
      :style="cssVars"
  >
    <!-- 标题区域 -->
    <div class="section-header">
      <div class="section-tag">03</div>
      <h3 class="section-title">赛制赛程</h3>
      <p class="section-desc">清晰赛程安排，助力选手高效备赛</p>
    </div>

    <!-- 赛程主体 - 弹性换行布局 -->
    <div class="schedule-container">
      <div
          class="schedule-item"
          v-for="(step, index) in props.data.steps"
          :key="index"
          :class="{ 'is-final': index === props.data.steps.length - 1 }"
      >
        <!-- 阶段标识 -->
        <div class="schedule-stage">
          <div class="stage-circle">
            {{ index + 1 }}
            <div class="stage-icon" v-if="index === props.data.steps.length - 1">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
          </div>
          <div class="stage-text">{{ step.stage }}</div>
        </div>

        <!-- 赛程内容卡片 -->
        <div class="schedule-card">
          <p class="card-desc">{{ step.desc }}</p>
        </div>

        <!-- 连接线 - 只在非最后一行且非最后一列显示 -->
        <div
            class="schedule-connector"
            v-if="index < props.data.steps.length - 1 && !isLastInRow(index)"
        >
          <div class="connector-dot"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue';

const props = defineProps({
  data: { type: Object, required: true },
  theme: { type: Object, required: true }
});

// 计算每行的步骤数（根据屏幕宽度和步骤数量自适应）
const stepsPerRow = computed(() => {
  const stepsCount = props.data.steps?.length || 0;
  if (stepsCount <= 3) return stepsCount;
  if (stepsCount <= 6) return 3;
  return 4;
});

// 检查是否是行尾步骤
const isLastInRow = (index) => {
  const rowIndex = Math.floor(index / stepsPerRow.value);
  const lastIndexInRow = (rowIndex + 1) * stepsPerRow.value - 1;
  return index === lastIndexInRow || index === props.data.steps.length - 1;
};

// 计算CSS变量
const cssVars = computed(() => {
  const primaryColor = props.theme.primaryColor || '#FF6B9D';

  // 计算颜色变体
  const hexToRgb = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  };

  const rgb = hexToRgb(primaryColor);

  return {
    '--primary-color': primaryColor,
    '--primary-rgb': rgb,
    '--primary-light': `rgba(${rgb}, 0.1)`,
    '--primary-dark': darkenColor(primaryColor, 10),
    '--steps-per-row': stepsPerRow.value
  };
});

// 辅助函数：加深颜色
function darkenColor(color, percent) {
  const num = parseInt(color.slice(1), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return '#' + (
      0x1000000 +
      (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)
  ).toString(16).slice(1);
}
</script>

<style scoped lang="scss">
// 使用CSS变量替代原来的SASS变量
.schedule-step {
  --bg-white: #FFFFFF;
  --text-dark: #333333;
  --text-gray: #666666;
  --text-light: #999999;
  --border-light: #F0F0F0;
  --border-medium: #E8E8E8;
  --shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  --shadow-hover: 0 6px 16px rgba(0, 0, 0, 0.08);
  --radius: 12px;
  --transition: all 0.3s ease;

  padding: 30px 20px;
  max-width: 1400px;
  margin: 0 auto;
}

// 标题栏优化
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 60px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-light);

  .section-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
    color: var(--bg-white);
    font-size: 20px;
    font-weight: 700;
    margin-right: 16px;
    box-shadow: 0 4px 8px rgba(var(--primary-rgb), 0.2);
  }

  .section-title {
    font-size: 28px;
    color: var(--text-dark);
    font-weight: 700;
    margin: 0;
    letter-spacing: -0.5px;
  }

  .section-desc {
    font-size: 15px;
    color: var(--text-gray);
    margin: 4px 0 0 20px;
    line-height: 1.5;
    padding-left: 20px;
    border-left: 3px solid var(--primary-light);
  }
}

// 赛程容器 - 弹性换行布局
.schedule-container {
  display: flex;
  flex-wrap: wrap;
  gap: 40px 20px; // 行间距40px，列间距20px
  justify-content: center; // 居中对齐
  position: relative;
  width: 100%;
}

// 赛程项 - 固定宽度，根据每行步骤数自适应
.schedule-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1 0 calc((100% / var(--steps-per-row)) - 20px);
  min-width: 280px; // 最小宽度
  max-width: 350px; // 最大宽度
  transition: var(--transition);
  margin-bottom: 20px;

  &:hover {
    .schedule-card {
      transform: translateY(-4px);
      box-shadow: var(--shadow-hover);
    }

    .stage-circle {
      transform: scale(1.1);
      box-shadow: 0 6px 12px rgba(var(--primary-rgb), 0.25);
    }
  }

  // 最终阶段特殊样式
  &.is-final {
    .stage-circle {
      background: linear-gradient(135deg, #FFD700, #FFA500);
      color: #8B4513;
      font-weight: 800;
      font-size: 18px;
      border: 2px solid #FFA500;
      box-shadow: 0 6px 15px rgba(255, 215, 0, 0.3);
    }

    .schedule-card {
      border-top: 4px solid #FFD700;
      background: linear-gradient(to bottom, #FFFDF0, #FFF8E1);
    }

    .stage-text {
      color: #B8860B;
      font-weight: 700;
    }
  }
}

// 阶段标识优化 - 垂直排列
.schedule-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 25px; // 增加与内容卡片的距离
  width: 100%;
  z-index: 2;

  .stage-circle {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: var(--primary-color);
    color: var(--bg-white);
    font-size: 20px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 15px; // 增加与标题的距离
    transition: var(--transition);
    position: relative;
    box-shadow: 0 4px 8px rgba(var(--primary-rgb), 0.2);

    .stage-icon {
      position: absolute;
      bottom: -4px;
      right: -4px;
      background: var(--bg-white);
      border-radius: 50%;
      padding: 2px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
  }

  .stage-text {
    font-size: 16px;
    color: var(--text-dark);
    font-weight: 600;
    text-align: center;
    width: 100%;
    line-height: 1.4;
    padding: 0 10px;
  }
}

// 赛程卡片优化
.schedule-card {
  background: var(--bg-white);
  border: 1px solid var(--border-medium);
  border-radius: var(--radius);
  padding: 30px 25px; // 增加内边距，让内容与边框有距离
  width: 100%;
  box-shadow: var(--shadow);
  transition: var(--transition);
  position: relative;
  overflow: hidden;
  min-height: 140px; // 设置最小高度
  display: flex;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: var(--primary-light);
  }

  .card-desc {
    font-size: 15px;
    color: var(--text-gray);
    line-height: 1.8;
    margin: 0;
    font-weight: 400;
    text-align: center;
    width: 100%;
  }
}

// 连接线优化 - 横向连接线
.schedule-connector {
  position: absolute;
  top: 25px; // 对齐圆形中心
  left: calc(100% + 10px);
  width: 40px;
  height: 2px;
  background: var(--border-light);
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg,
        rgba(var(--primary-rgb), 0.3) 0%,
        rgba(var(--primary-rgb), 0.6) 50%,
        rgba(var(--primary-rgb), 0.3) 100%);
  }

  .connector-dot {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
  }
}

// 响应式设计
@media (max-width: 1200px) {
  .schedule-container {
    --steps-per-row: 3; // 大屏幕显示3列
  }

  .schedule-item {
    flex: 1 0 calc(33.333% - 20px);
  }

  .schedule-connector {
    width: 30px;
  }
}

@media (max-width: 992px) {
  .schedule-container {
    --steps-per-row: 2; // 中等屏幕显示2列
    gap: 30px 15px;
  }

  .schedule-item {
    flex: 1 0 calc(50% - 15px);
    min-width: 250px;
  }

  .schedule-connector {
    // 隐藏横向连接线，改用垂直连接线
    display: none;
  }

  // 在移动端添加垂直连接线
  .schedule-item:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: -25px;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 30px;
    background: linear-gradient(to bottom,
        rgba(var(--primary-rgb), 0.3) 0%,
        rgba(var(--primary-rgb), 0.6) 50%,
        rgba(var(--primary-rgb), 0.3) 100%);
  }
}

@media (max-width: 768px) {
  .schedule-step {
    padding: 20px 16px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 40px;

    .section-desc {
      margin-left: 0;
      padding-left: 16px;
      border-left: 2px solid var(--primary-light);
    }
  }

  .schedule-container {
    --steps-per-row: 1; // 小屏幕显示1列
    flex-direction: column;
    gap: 30px;
  }

  .schedule-item {
    flex: 1 0 100%;
    max-width: 100%;
    margin-bottom: 0;
  }

  .schedule-card {
    padding: 25px 20px;
    min-height: 120px;
  }

  // 移除垂直连接线
  .schedule-item::after {
    display: none;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 24px;
  }

  .schedule-card {
    padding: 20px;
    min-height: 110px;
  }

  .card-desc {
    font-size: 14px;
    line-height: 1.6;
  }

  .stage-circle {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .stage-text {
    font-size: 15px;
  }
}
</style>